<template>
  <div class="content">
    <mu-content-block v-if="isLoading" style="text-align: center">
      <mu-circular-progress :size="48"></mu-circular-progress>
      <br/>
      <span v-text="$t('message.loadingText')"></span>
    </mu-content-block>
    <div v-if="!isLoading && user.scope">
      <div class="headerGroup">
        <edit-question-title
          v-if="data.question.user.user_id === user.scope.user_id"
          :question-id="data.question.question_id"
          :title="data.question.question"
          @questionUpdated="questionDidUpdate"
        ></edit-question-title>
        <h1 v-else v-html="$sanitize(data.question.question)"></h1>
        <div class="thread-date">{{ $t('message.threadAskedBy') }} <router-link :to="{path: '/profile/' + data.question.user.profile.url}">{{encodeText(data.question.user.display_name)}}</router-link> on {{formatDate(data.question.timestamp)}}</div>
      </div>
      <mu-card>
        <mu-card-text class="userCard">
          <div
            class="userImage"
            :class="{ 'has-photo': !!data.question.user.avatar }"
            :style="data.question.user.avatar ? { backgroundImage: 'url(' + data.question.user.avatar + ')' } : null"
          >
            <span v-if="!data.question.user.avatar">{{ avatarInitials(data.question.user.display_name) }}</span>
          </div>
          <div class="userBlock">
            <div class="badges">
              <user-badge v-if="data.question.user.is_admin" type="support" mode="image"></user-badge>
              <user-badge v-if="data.question.user.is_mentor" type="support" mode="image"></user-badge>
              <user-badge v-if="data.question.user.is_moderator" type="support" mode="image"></user-badge>
              <user-badge v-if="data.question.user.is_champion" type="champion" mode="image"></user-badge>
              <user-badge v-if="data.question.user.is_complete" type="finished-program" mode="image"></user-badge>
            </div>
            <div>
              <router-link :to="{path: '/profile/' + data.question.user.profile.url}">
                {{encodeText(data.question.user.display_name)}} <country-badge v-if="data.question.user.country.country_id !== 0" :alpha2="data.question.user.country.alpha2" :tooltip="data.question.user.country.country_name"></country-badge>
              </router-link>
              <user-badge class="hcpBadge" v-if="data.hcp_question && data.question.user.is_hcp && data.question.user.hcp_verified" type="hcp-verified" mode="image"></user-badge>
            </div>
            <div class="blueLine" v-text="data.hcp_question && data.question.user.is_hcp ? data.question.user.hcp_role : data.question.user.diabetes_type"></div>
            <mu-row>
              <mu-col width="100" tablet="50" desktop="50">
                <div class="posts">{{$t('message.threadPostsCount')}} {{data.question.posts}}</div>
              </mu-col>
              <mu-col width="100" tablet="50" desktop="50">
                <div class="date" v-text="formatDate(data.question.timestamp)"></div>
              </mu-col>
            </mu-row>
          </div>
        </mu-card-text>
        <mu-card-text v-if="!isQuestionEditOpen">
          <vue-markdown :source="encodeText(data.question.content || data.question.question)"
                        style="white-space: pre-wrap;">
          </vue-markdown>
        </mu-card-text>
        <mu-card-text v-if="isQuestionEditOpen">
          <form @submit.prevent="updateQuestion">
            <mu-text-field
              v-validate="'required'"
              data-vv-scope="editQuestion"
              data-vv-name="question"
              data-vv-validate-on="none"
              data-vv-value-path="inputValue"
              :errorText="errors.first('question')"
              :data-vv-as="$t('message.labelBody')"
              v-model="questionEditModel.content"
              :labelFloat="false"
              multiLine
              :rows="1"
              :rowsMax="10"
              :hintText="$t('message.labelBody')"
              :fullWidth="true"
              class="textarea"
            ></mu-text-field>
            <button type="submit" style="display: none"></button>
            <mu-flat-button
              :label="$t('message.cancelText')"
              @click.native="toggleEditQuestion"
            ></mu-flat-button>
            <mu-raised-button
              @click.native="updateQuestion()"
              :label="$t('message.updateText')"
              secondary>
            </mu-raised-button>
          </form>
        </mu-card-text>
        <div v-if="!isQuestionEditOpen">
          <mu-card-text class="likesCounter"
            v-if="data.question.likes !== 0"
            v-html="$sanitize(pluralizeUseful(data.question.likes, data.question.liked))"
          ></mu-card-text>
          <mu-card-actions class="actions" align="left">
            <mu-flat-button
              color="#D83E38"
              class="reportBtn"
              :disabled="data.question.reported"
              v-if="(user.user_role.role_id !== 2 && data.question.user.is_admin !== true) && data.question.user.user_id !== user.scope.user_id"
              icon="report_problem"
              :label="data.question.reported ? $t('message.reportedText') : $t('message.reportText')"
              @click="openReport('THREAD_QUESTION', data.question.question_id)">
            </mu-flat-button>
            <mu-flat-button
              color="#0179d9"
              icon="thumb_up"
              @click.native="markQuestionUseful"
              :label="$t('message.discussionTabUseful')"
              v-if="data.question.user.user_id !== user.scope.user_id"
              v-bind:class="{active: data.question.liked}"
              :disabled="isLiking"
            ></mu-flat-button>
            <mu-flat-button
              color="#0179d9"
              @click.native="toggleEditQuestion()"
              v-if="data.question.user.user_id === user.scope.user_id"
              icon="edit"
              :label="$t('message.editText')"
            ></mu-flat-button>
            <mu-flat-button
              color="#0179d9"
              @click.native="deleteQuestion(data.question.question_id)"
              v-if="data.question.user.user_id === user.scope.user_id || user.user_role.role_id === 2" icon="delete"
              :label="$t('message.deleteText')"
            ></mu-flat-button>
          </mu-card-actions>
        </div>
      </mu-card>
      <report
        v-if="data.question.report_count > 0 && user.user_role.role_id === 2"
        type="THREAD_QUESTION"
        :typeId="parseInt($route.params.id)"
      ></report>
      <div id="replies" v-if="!isLoading && data.replies.length > 0">
        <mu-row gutter>
          <mu-col desktop="50" width="100" tablet="50">
            <mu-flexbox>
              <mu-flexbox-item>
                <div class="pagination">
                  <mu-pagination
                    v-if="data.replies.length !== 0"
                    :total="data.count_replies"
                    :current="page"
                    :defaultPageSize="per_page"
                    :pageSize="per_page"
                    @pageChange="handlePageChange">
                  </mu-pagination>
                </div>
              </mu-flexbox-item>
            </mu-flexbox>
          </mu-col>
          <mu-col desktop="25" width="50" tablet="25">
            <mu-select-field
              :scroller="scroller"
              underlineFocusClass="underlineFocus"
              v-model="orderBy"
              fullWidth
            >
              <mu-menu-item value="CHRONOLOGICAL" :title="$t('message.threadChronological')"></mu-menu-item>
              <mu-menu-item value="MOST_RECENT" :title="$t('message.threadMostRecent')"></mu-menu-item>
            </mu-select-field>
          </mu-col>
          <mu-col desktop="25" width="50" tablet="25">
            <mu-select-field
              :scroller="scroller"
              underlineFocusClass="underlineFocus"
              v-model="per_page"
              @change="updatePerPage()"
              fullWidth
            >
              <mu-menu-item
                v-for="item, index in pageSizes"
                :key="'page-size-' + index"
                :value="item"
                :title="item + ' per page'"
              />
            </mu-select-field>
          </mu-col>
        </mu-row>
        <mu-row v-for="thread, index in data.replies" :key="'reply-' + index">
          <mu-col desktop="100" width="100" tablet="100">
            <mu-card
              v-bind:class="{'bottom': index !== (data.replies.length - 1) && user.user_role.role_id !== 2 || thread.report_count === 0}">
              <mu-card-text class="userCard">
                <div
                  class="userImage"
                  :class="{ 'has-photo': !!thread.user.avatar }"
                  :style="thread.user.avatar ? { backgroundImage: 'url(' + thread.user.avatar + ')' } : null"
                >
                  <span v-if="!thread.user.avatar">{{ avatarInitials(thread.user.display_name) }}</span>
                </div>
                <div class="userBlock">
                  <div class="badges">
                    <user-badge v-if="thread.user.is_admin" type="support" mode="image"></user-badge>
                    <user-badge v-if="thread.user.is_mentor" type="support" mode="image"></user-badge>
                    <user-badge v-if="thread.user.is_moderator" type="support" mode="image"></user-badge>
                    <user-badge v-if="thread.user.is_champion" type="champion" mode="image"></user-badge>
                    <user-badge v-if="thread.user.is_complete" type="finished-program" mode="image"></user-badge>
                  </div>
                  <div>
                    <router-link :to="{path: '/profile/' + thread.user.profile.url}">
                      {{encodeText(thread.user.display_name)}} <country-badge v-if="thread.user.country.country_id !== 0" :alpha2="thread.user.country.alpha2" :tooltip="thread.user.country.country_name"></country-badge>
                    </router-link>
                    <user-badge class="hcpBadge" v-if="data.hcp_question && thread.user.is_hcp && thread.user.hcp_verified" type="hcp-verified" mode="image"></user-badge>
                  </div>
                  <div class="blueLine" v-text="data.hcp_question && thread.user.is_hcp ? thread.user.hcp_role : thread.user.diabetes_type"></div>
                  <mu-row>
                    <mu-col width="100" tablet="50" desktop="50">
                      <div class="posts">{{$t('message.threadPostsCount')}} {{thread.posts}}</div>
                    </mu-col>
                    <mu-col width="100" tablet="50" desktop="50">
                      <div class="date" v-text="formatDate(thread.timestamp)"></div>
                    </mu-col>
                  </mu-row>
                </div>
              </mu-card-text>
              <mu-card-text class="quotes" v-for="reply, index in thread.answer_quotes" :key="'reply' + index" v-if="reply.display_name">
                <mu-card>
                  <mu-card-header>
                    <div v-html="$sanitize(reply.display_name + ' ' + $t('message.threadUserSaid') + ' &uarr;')"></div>
                  </mu-card-header>
                  <mu-card-text>
                    <vue-markdown :source="$sanitize(reply.answer.substr(0, 300) + ' ...')"></vue-markdown>
                  </mu-card-text>
                </mu-card>
              </mu-card-text>
              <mu-card-text>
                <vue-markdown v-if="!thread.editOpen"
                              style="white-space: pre-wrap;"
                              :source="escape($sanitize(thread.answer))">

                </vue-markdown>
              </mu-card-text>
              <mu-card-text v-if="thread.editOpen">
                <form v-validate @submit.prevent="updateAnswer(thread)" data-vv-scope="updateAnswer">
                  <mu-text-field
                    v-if="thread.editOpen"
                    v-bind:id="'reply-' + thread.answer_id"
                    v-validate="'required|min:5'"
                    data-vv-validate-on="none"
                    :data-vv-name="'reply-' + thread.answer_id"
                    data-vv-value-path="inputValue"
                    :data-vv-as="$t('message.labelBody')"
                    :errorText="errors.first('reply-' + thread.answer_id)"
                    v-model="thread.editableReply"
                    :labelFloat="false"
                    multiLine
                    :rows="1"
                    :rowsMax="10"
                    :hintText="$t('message.replyToText') + ' ' + thread.user.display_name"
                    :fullWidth="true"
                    :readonly="isSaving"
                    :disabled="isSaving"
                    class="textarea"
                  ></mu-text-field>
                  <mu-flat-button
                    @click.native="discardEdit(thread)"
                    :label="$t('message.discardText')"
                  ></mu-flat-button>
                  <button type="submit" style="display: none;" />
                  <mu-raised-button
                    secondary
                    :disabled="thread.isUpdating"
                    @click.native="updateAnswer(thread)"
                    :label="thread.isUpdating ? $t('message.updatingText') + '...' : $t('message.updateText')"
                  ></mu-raised-button>
                </form>
              </mu-card-text>
              <mu-card-text class="likesCounter"
                v-if="thread.likes !== 0"
                v-html="$sanitize(pluralizeUseful(thread.likes, thread.liked))"
              ></mu-card-text>
              <mu-card-actions class="actions" align="left">
                <span class="index">#{{ getPageIndex(index) }}</span>
                <mu-flat-button
                  color="#D83E38"
                  class="reportBtn"
                  :disabled="thread.reported"
                  v-if="(user.user_role.role_id !== 2 && thread.user.is_admin !== true) && thread.user.user_id !== user.scope.user_id"
                  icon="report_problem"
                  :label="thread.reported ? $t('message.reportedText') : $t('message.reportText')"
                  @click="openReport('THREAD_ANSWER', thread.answer_id)"
                />
                <mu-flat-button
                  color="#0179d5"
                  v-bind:class="{active: thread.liked}"
                  icon="thumb_up"
                  :label="$t('message.discussionTabUseful')"
                  v-if="user.scope.user_id !== thread.user.user_id"
                  @click.native="markUseful(thread)"
                  :disabled="thread.isLiking"
                />
                <mu-flat-button
                  color="#0179d5"
                  icon="edit"
                  v-if="user.scope.user_id === thread.user.user_id"
                  @click.native="editAnswer(thread)"
                  label="Edit"
                />
                <mu-flat-button
                  color="#0179d5"
                  icon="delete"
                  @click.native="deleteAnswer(thread, thread.answer_id)"
                  v-if="user.scope.user_id === thread.user.user_id || user.user_role.role_id === 2"
                  :label="$t('message.deleteText')"
                />
                <mu-flat-button
                  color="#0179d5"
                  icon="reply"
                  :label="capitalize($t('message.replyText'))"
                  @click.native="toggleReplyPanel(thread)"
                />
              </mu-card-actions>
              <mu-card-text v-if="thread.replyOpen">
                <mu-text-field
                  v-bind:id="'reply-' + thread.answer_id"
                  v-validate="'required'"
                  :data-vv-name="'reply-' + thread.answer_id"
                  data-vv-value-path="inputValue"
                  :errorText="errors.first('reply-' + thread.answer_id)"
                  :data-vv-as="$t('message.labelBody')"
                  v-model="thread.replyable"
                  :labelFloat="false"
                  multiLine
                  :rows="1"
                  :rowsMax="10"
                  :hintText="$store.state.status.payment.paid ? $t('message.replyToText') + ' ' + thread.user.display_name : 'Only premium users can answer a question'"
                  :fullWidth="true"
                  :readonly="isSaving"
                  :disabled="isSaving"
                  class="textarea"
                ></mu-text-field>
              </mu-card-text>
              <mu-card-actions v-show="thread.replyOpen" align="right">
                <mu-flat-button @click.native="toggleReplyPanel(thread)" :label="$t('message.discardText')"></mu-flat-button>
                <mu-flat-button
                  @click.native="replyToAnswer(thread)"
                  color="white"
                  backgroundColor="#7CBE30"
                  :label="thread.isSaving ? $t('message.savingText') : $t('message.submitText')"
                  :disabled="thread.isSaving || !$store.state.status.payment.paid"
                ></mu-flat-button>
              </mu-card-actions>
            </mu-card>
            <report
              v-if="thread.report_count > 0 && user.user_role.role_id === 2"
              type="THREAD_ANSWER"
              :typeId="thread.answer_id"
            ></report>
          </mu-col>
        </mu-row>
        <div class="pagination">
          <mu-pagination
            v-if="data.replies.length !== 0"
            class="top"
            :total="data.count_replies"
            :current="page"
            :defaultPageSize="per_page"
            :pageSize="per_page"
            @pageChange="handlePageChange">
          </mu-pagination>
        </div>
      </div>
      <mu-card v-if="!isLoading && data.replies.length === 0" class="top">
        <mu-card-text>
          {{ $t('message.threadAnswersEmpty') }}
        </mu-card-text>
      </mu-card>
      <div class="navigate">
        <router-link :to="{ name: 'QuestionThread', params: { id : data.prev } }" v-if="data.prev">{{$t('message.threadPreviousQuestion')}}</router-link>
        <span class="sep" v-if="data.prev != 0 && data.next != 0">|</span>
        <router-link :to="{ name: 'QuestionThread', params: { id : data.next } }" v-if="data.next">{{$t('message.threadNextQuestion')}}</router-link>
      </div>
      <form id="submitReplyForm" @submit.prevent="saveAnswer">
        <!--<div class="tools">
          <mu-icon-button icon="local_movies" @click="openEmbedModal" />
        </div>-->
        <mu-card>
          <mu-card-text>
            <mu-text-field
              v-validate="'required'"
              data-vv-name="answer"
              data-vv-value-path="inputValue"
              data-vv-validate-on="none"
              :data-vv-as="$t('message.labelBody')"
              :errorText="errors.first('answer')"
              v-model="model.answer"
              :labelFloat="false"
              multiLine
              :rows="3"
              :rowsMax="6"
              :hintText="$store.state.status.payment.paid ? $t('message.threadInputPlaceholder') : 'Only premium users can ask a question'"
              fullWidth
              :readonly="isSaving"
              :disabled="isSaving"
              class="textarea"
            ></mu-text-field>
            <div class="action">
              <mu-flat-button
                class="button"
                @click.native="saveAnswer"
                backgroundColor="#3d8517"
                color="white"
                :label="isSaving ? $t('message.savingText') : $t('message.threadSubmitReplyText')"
                :disabled="isSaving || !$store.state.status.payment.paid"
              />
            </div>
          </mu-card-text>
          <!--<mu-card-text>
            <div id="embedVideo" v-if="embedObject.embed_url">
              <iframe-video-player :src="embedObject.embed_url" />
            </div>
          </mu-card-text>-->
        </mu-card>
      </form>
      <!--<mu-dialog id="embedModal" :open="showEmbed" title="Embed Media" @close="closeEmbedModal">
        <mu-text-field v-model="embedUrl" hintText="Enter a media url" fullWidth />
        <mu-flat-button slot="actions" @click="getEmbed" primary label="Embed"/>
        <mu-flat-button slot="actions" primary @click="closeEmbedModal" label="Cancel"/>
      </mu-dialog>-->
      <report-modal
        v-if="activeReport.type && activeReport.type_id"
        :open="activeReport.open"
        :type="activeReport.type"
        :type_id="activeReport.type_id"
        @onClose="activeReport.open = false"
        @onComplete="setReported(activeReport)"
      />
      <div v-if="isLoading" align="center" style="margin-top: 34px">
        <mu-circular-progress color="#1FA3FF" :size="40"></mu-circular-progress>
        <div v-text="$t('message.loadingText')"></div>
      </div>
      <mu-snackbar
        v-if="isNewMessage"
        message="1 new reply - reload the page?"
        :action="$t('message.okText')"
        @actionClick="loadData({page: 1, per_page: 10, latest: 'true'}, true)"
        actionColor="#1FA3FF" @close="hideToast"/>
      <mu-toast v-if="answerRemoved" message="Your answer was removed"/>
    </div>
  </div>
</template>
<script>
  import config from '../../config'
  import ScrollerMixin from '../../mixins/ScrollerMixin'
  import UserManager from "../../helpers/UserManager"
  import TitleManager from '../../helpers/TitleManager'
  import Pusher from '../../helpers/PusherManager'
  import HTMLStringParser from '../../helpers/HTMLStringParser'
  import DiscussionApi from '../../api/Discussion';
  import UserBadge from "../../components/UserBadge/UserBadge.vue";
  import CountryBadge from "../../components/CountryBadge/CountryBadge.vue"
  import ReportingMixin from "../../mixins/ReportingMixin"
  import Report from "../../components/Reporting/Report.vue";
  import ReportModal from "../../components/Reporting/Reporting.vue";
  import EditQuestionTitle from "../../components/Questions/EditQuestionTitle/EditQuestionTitle";
  import "./Questions.less";
  import axios from 'axios';
  import IframeVideoPlayer from "../../components/IframeVideoPlayer/IframeVideoPlayer";
  import HCPMixin from "../../mixins/HCPMixin";
  import MuCardText from "muse-ui/src/card/cardText";
  import formatDate from '../../helpers/FormatDate'

  let pageSizes = [
    10,
    20,
    40
  ];

  export default {
    components: {
      MuCardText,
      IframeVideoPlayer,
      EditQuestionTitle,
      Report,
      ReportModal,
      UserBadge,
      CountryBadge,
    },
    name: 'questions-thread',
    mixins: [ScrollerMixin, ReportingMixin, HCPMixin],
    data() {
      return {
        showEmbed: false,
        embedUrl: '',
        embedObject: {},
        page: 1,
        per_page: 10,
        orderBy: 'CHRONOLOGICAL',
        pageSizes: pageSizes,
        isQuestionEditOpen: false,
        questionEditModel: {},
        isLoading: true,
        isSaving: false,
        isReplySaving: false,
        isLiking: false,
        isNewMessage: false,
        answerRemoved: false,
        leftTop: {horizontal: 'left', vertical: 'top'},
        leftBottom: {horizontal: 'left', vertical: 'bottom'},
        rightBottom: {horizontal: 'right', vertical: 'bottom'},
        rightTop: {horizontal: 'right', vertical: 'top'},
        data: {
          replies: []
        },
        model: {
          question_id: parseInt(this.$route.params.id)
        }
      }
    },
    mounted() {
      let self = this;
      if(self.$store.state.status.payment.paid === false) {
        self.$router.push({
          name: 'QuestionsIndex'
        });
      }
      const channel = Pusher.subscribe('discussion_' + this.$route.params.id);
      channel.bind('reply_create', function (data) {
        if (data.last_id.user.user_id !== self.user.scope.user_id) {
          self.isNewMessage = true;
        }
      });
      self.page = parseInt(self.$route.query.page ? self.$route.query.page : self.page);
      self.per_page = parseInt(self.$route.query.per_page ? self.$route.query.per_page : self.per_page);
      if (self.orderBy === 'CHRONOLOGICAL') {
        self.loadData({
          page: self.page,
          per_page: self.per_page
        });
      }
      else {
        self.loadData({
          page: self.page,
          per_page: self.per_page,
          latest: true
        });
      }
    },
    watch: {
      '$route': function (newRoute, oldRoute) {
        let self = this;
        self.page = parseInt(self.$route.query.page ? self.$route.query.page : self.page);
        self.per_page = parseInt(self.$route.query.per_page ? self.$route.query.per_page : self.per_page);
        if (self.orderBy === 'CHRONOLOGICAL') {
          self.loadData({
            page: self.page,
            per_page: self.per_page
          });
        }
        else {
          self.loadData({
            page: self.page,
            per_page: self.per_page,
            latest: 'true'
          });
        }
      },
      'orderBy': function () {
        let self = this;
        self.page = parseInt(self.$route.query.page ? self.$route.query.page : self.page);
        self.per_page = parseInt(self.$route.query.per_page ? self.$route.query.per_page : self.per_page);
        if (self.orderBy === 'CHRONOLOGICAL') {
          self.loadData({
            page: self.page,
            per_page: self.per_page,
            softLoad: true
          });
        }
        else {
          self.loadData({
            page: self.page,
            per_page: self.per_page,
            latest: true,
            softLoad: true,
          });
        }
      },
      'per_page': function () {
        let self = this;
        self.$router.replace({
          query: {
            page: self.page,
            per_page: self.per_page
          }
        })
      }
    },
    methods: {
      capitalize(str) {
        return _.capitalize(str);
      },
      updatePerPage() {
        let self = this;
        self.page = 1;
      },
      getPageIndex(index) {
        let self = this,
            i = (self.per_page * self.page) - (self.per_page) + 1;
        if ( i !== 0) {
          return i += index;
        }
      },
      escape(str) {
        str = this.$sanitize(HTMLStringParser.encode(str));
        return _.unescape(str);
      },
      openEmbedModal () {
        let self = this;
        self.showEmbed = true;
      },
      closeEmbedModal () {
        let self = this;
        self.showEmbed = false;
        self.embedUrl = "";
      },
      getEmbed() {
        let self = this;
        if ( self.isValidUrl(self.embedUrl) == 1 ) {
          axios.get(config.apiUrl + 'video/parse?url=' + self.embedUrl).then(response => {
            self.embedObject = response.data;
            self.closeEmbedModal();
          });
        }
      },
      isValidUrl(url) {
        if(/^(http|https|ftp):\/\/[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i.test(url)) {
          return true;
        } else {
          return false;
        }
      },
      questionDidUpdate(value) {
        let self = this;
        self.data.question.question = value;
        TitleManager.setTitle(self.data.question.question);
      },
      updateQuestion() {
        let self = this;
        if (self.questionEditModel.content.trim() === '') {
          self.$toastr.error('Your post cannot be empty');
          return;
        }
        DiscussionApi.updateQuestion(self.questionEditModel)
          .then(function () {
            self.data.question.content = self.questionEditModel.content.trim();
            self.toggleEditQuestion();
          })
      },
      toggleEditQuestion() {
        let self = this;
        let question = JSON.parse(JSON.stringify(self.data.question));
        self.questionEditModel = {
          question_id: question.question_id,
          content: question.content
        };
        self.isQuestionEditOpen = !self.isQuestionEditOpen;
      },
      encodeText(string) {
        let sanitized = this.$sanitize(string);
        return HTMLStringParser.encode(sanitized);
      },
      // Initials for the avatar fallback (when a user has no photo). Uses the
      // first + last word of the display name; degrades to a single letter, or
      // '?' when there's no usable name at all.
      avatarInitials(name) {
        if (!name) return '?';
        let parts = ('' + name).trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return '?';
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      },
      markQuestionUseful() {
        let self = this;
        DiscussionApi.likeDiscussionQuestion(self.data.question.question_id).then(function(response) {
          self.data.question.liked = response.data.like_status;
          if (self.data.question.liked === true) {
            self.data.question.likes++;
            self.isLiking = false;
          } else {
            self.data.question.likes--;
            self.isLiking = false;
          }
        });
      },
      markUseful(answer) {
        let self = this;
        answer.isLiking = true;
        self.$forceUpdate();
        DiscussionApi.likeDiscussionReply(answer.answer_id).then(function (response) {
          answer.liked = response.data.like_status;
          if (answer.liked === true) {
            answer.likes++;
            answer.isLiking = false;
          } else {
            answer.likes--;
            answer.isLiking = false;
          }
          self.$forceUpdate();
        });
      },
      saveAnswer() {
        let self = this;
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid) {
              self.isSaving = true;
              DiscussionApi.postDiscussionReply(self.model)
                .then(function (response) {
                  if (self.data.replies.length == 0) {
                    self.loadData({
                      page: 1,
                      per_page: 10,
                      latest: 'true',
                      softLoad: true
                    });
                    self.isSaving = false;
                    self.model.answer = '';
                    self.$nextTick(function () {
                      self.resetErrors();
                    });
                    self.$forceUpdate();
                  }
                  else {
                    self.data.replies.push(response.data.last_id);
                    self.isSaving = false;
                    self.model.answer = '';
                    self.$nextTick(function () {
                      self.resetErrors();
                    });
                    self.$forceUpdate();
                  }
                })
            }
          });
      },
      editAnswer(answer) {
        let self = this;
        answer.editOpen = true;
        answer.editableReply = _.unescape(this.$sanitize(answer.answer));
        self.$forceUpdate();
        let timeout = setTimeout(function () {
          let element = document.getElementById('reply-' + answer.answer_id)
            .children[0]
            .children[1]
            .children[1];
          $(element).focus();
          clearTimeout(timeout);
        })
      },
      discardEdit(answer) {
        let self = this;
        answer.editOpen = false;
        self.$forceUpdate();
      },
      updateAnswer(answer) {
        let self = this;
        self.$validator.validateAll('updateAnswer').then(function (valid) {
          if (valid === true) {
            answer.answer = answer.editableReply;
            answer.isUpdating = true;
            self.$forceUpdate();
            DiscussionApi.updateAnswer(answer)
              .then(function (response) {
                answer.editOpen = false;
                answer.isUpdating = false;
                self.$forceUpdate();
              })
          }
        });
      },
      deleteAnswer(answer, answer_id) {
        let self = this;

        self.$alert({
          title: self.$t('message.confirmRemoveTitle'),
          text: self.$t('message.confirmRemoveAnswerItem'),
          type: "warning",
          showCancelButton: true,
          showLoaderOnConfirm: false,
          confirmButtonColor: "white",
          cancelButtonColor: "white",
          preConfirm: function () {
            return DiscussionApi.deleteAnswer(answer_id)
          }
        })

          .then(function (response) {
            let index = self.data.replies.indexOf(answer);
            self.data.replies.splice(index, 1);
            self.$alert({
              title: self.$t('message.labelSuccess'),
              text: self.$t('message.confirmedRemoveAnswerItem'),
              type: "success",
              showCancelButton: false,
              confirmButtonColor: "white"
            });
          }.bind(self))
          .catch(self.$alert.noop);
      },
      hideToast() {
        let self = this;
        self.isNewMessage = false;
      },
      toggleReplyPanel(answer) {
        let self = this;
        if (answer.replyOpen) {
          answer.replyOpen = !answer.replyOpen;
          self.$forceUpdate();
        }
        else {
          let repliesLength = self.data.replies.length;
          for (let i = 0; i < repliesLength; i++) {
            self.data.replies[i].replyOpen = false;
          }
          answer.replyOpen = true;
          self.$forceUpdate();
          let timeout = setTimeout(function () {
            let element = document.getElementById('reply-' + answer.answer_id)
              .children[0]
              .children[1]
              .children[1];
            let timeout = setTimeout(function () {
              $('.main-content').animate({
                scrollTop: $("#reply-" + answer.answer_id).offset().top - $('.main-content').offset().top + $('.main-content').scrollTop() - 16,
              }, 200, function () {
                $(element).focus();
              });
              clearTimeout(timeout);
            }.bind(self), 100);
          })
        }
      },
      replyToAnswer(answer) {
        let self = this;
        if (answer.replyable) {
          answer.isSaving = true;
          self.$forceUpdate();
          let model = {
            question_id: this.$route.params.id,
            answer: answer.replyable,
            replies: [
              {
                answer_id: answer.answer_id
              }
            ]
          };
          DiscussionApi.postDiscussionReply(model)
            .then(function (response) {
              self.data.replies.unshift(response.data.last_id);
              answer.isSaving = false;
              answer.replyOpen = false;
              answer.replyable = '';
              self.$nextTick(function () {
                // set flags for scoped field.
                self.resetErrors();
              });
              self.$forceUpdate();
            })
        }
      },
      resetErrors() {
        let self = this;
        self.errors.clear()
      },
      formatDate(date) {
        return formatDate(date);
      },
      handlePageChange(page) {
        let self = this;
        self.$router.replace({
          query: {
            page: page,
            per_page: self.per_page
          }
        })
      },
      loadData(queryModel, fromToast = false) {
        let self = this;
        if (!queryModel.softLoad) {
          TitleManager.setTitle(self.$t('message.loadingText'));
          self.isLoading = true;
        }

        if (fromToast === true) {
          self.isNewMessage = false;
        }

        DiscussionApi.getDiscussionThread(self.$route.params.id, queryModel)
          .then(function (response) {
            self.data = response.data.data;
            TitleManager.setTitle(self.data.question.question);
            self.isLoading = false;
            self.isSaving = false;
            self.model.answer = '';
            self.$nextTick(function () {
              // set flags for scoped field.
              self.resetErrors();
            })
          })
          .catch(function (error) {
            if (error.response.status === 404) {
              self.$router.push({
                name: '404'
              })
            }
            // redirect to discussion if patient trying to view hcp question
            if (error.response.status === 403) {
              self.$router.push({
                name: 'QuestionsIndex'
              })
            }
          })
        setTimeout(function() {
          var repliesEl = document.getElementById('replies');
          if (!repliesEl) {
            return;
          }
          var anchors = repliesEl.getElementsByTagName('a');

          for (var i=0; i<anchors.length; i++){
            anchors[i].setAttribute('target', '_blank');
          }
        }, 500);

      },
      pluralizeUseful(count, liked) {
        let self = this;
        let plural;
        if (count === 1) {
          if (liked)
            plural = '<strong>'+ self.$t('message.youText') +'</strong> ' + self.$t('message.threadSingleUseful');
        else
            plural = '<strong>' + count + '</strong>' + ' ' + self.$t('message.threadUseful');
        }
        if (count > 1) {
          if (liked)
            plural = '<strong>' + self.$t('message.youText') + '</strong> ' + self.$t('message.andText') + ' <strong> ' + (count - 1) + '</strong> ' + self.$t('message.discussionYouAndOthersFoundUseful');
          else
            plural = '<strong>' + count + '</strong>' + ' ' + self.$t('message.threadUsefulPlural');
        }
        return plural;
      },
      getInitialsFromName(name, delimeter) {
        if (name) {
          var array = name.split(delimeter);
          switch (array.length) {
            case 1:
              return array[0].charAt(0).toUpperCase();
              break;
            default:
              return array[0].charAt(0).toUpperCase() + array[array.length - 1].charAt(0).toUpperCase();
          }
        }
        return false;
      },
      deleteQuestion(question_id) {
        let self = this;
        self.$alert({
          title: self.$t('message.confirmRemoveTitle'),
          text: self.$t('message.confirmRemoveQuestionItem'),
          type: "warning",
          showCancelButton: true,
          showLoaderOnConfirm: false,
          confirmButtonColor: "white",
          cancelButtonColor: "white",
          preConfirm: function () {
            return DiscussionApi.deleteQuestion(question_id)
          }
        })
          .then(function (response) {
            self.$router.push({
              name: 'QuestionsIndex'
            });
            self.$alert({
              title: self.$t('message.labelSuccess'),
              text: self.$t('message.confirmedRemoveQuestionItem'),
              type: "success",
              showCancelButton: false,
              confirmButtonColor: "white"
            });
          }.bind(self))
          .catch(self.$alert.noop)
      },
      setReported() {
        let self = this;
        if (self.activeReport.type === 'THREAD_ANSWER') {
          let currentThread = self.data.replies.filter(function (item) {
            return item.answer_id === self.activeReport.type_id
          })[0];
          currentThread.reported = true;
        }
        else {
          self.data.question.reported = true;
        }
        self.$forceUpdate();
      }
    },
    computed: {
      user: {
        get() {
          let self = this;
          return self.$store.state.user;
        },
        set() {
          let self = this;
          return self.$store.state.user;
        }
      }
    }
  }
</script>
<style lang="less">
  @import "../../styles/theme.less";
  @branding: #1FA3FF;
  @usefulActive: #7CBE30;

  #replies {
    .mu-card-text {
      p {
        a {
          &::after {
            font-family: 'Material Icons';
            content: "launch";
            -webkit-font-feature-settings: 'liga';
            vertical-align: top;
            padding-left: 5px;
          }
        }
      }
    }
  }


  @media (min-width: 480px) and (max-width: 768px) {
    div#question-thread .largeTitle {
      font-size: 20px !important;
    }
  }

  #question-thread {

    // Avatar: fixed 105x105 circle. Photos are drawn as a cover background so
    // non-square images never stretch; with no photo we show the user's
    // initials on a branded circle instead of a generic placeholder image.
    .userCard .userImage {
      width: 105px;
      height: 105px;
      flex-shrink: 0;
      border-radius: 50%;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #0179d5;
      background-size: cover;
      background-position: center center;
      background-repeat: no-repeat;
      color: #fff;
      font-size: 38px;
      font-weight: 600;
      line-height: 1;
      text-transform: uppercase;
      user-select: none;

      @media (max-width: 600px) {
        width: 72px;
        height: 72px;
        font-size: 26px;
      }

      @media (max-width: 480px) {
        width: 56px;
        height: 56px;
        font-size: 22px;
      }
    }
    pre {
     white-space: normal;
     word-break: break-all;margin: 0;
      code {
        font-family: 'Poppins', Lato, sans-serif;
      }
    }

  .mu-card-title.blueTitle {
    font-weight: normal;
    margin-bottom: 8px;
  }

  button.mu-flat-button.active {
    color: @usefulActive;
  }
  .mu-text-field.focus-state {
    color: @branding;
  }
  .mu-text-field-label {
    color: @branding !important;
  }
  .mu-text-field-focus-line {
    background-color: @branding !important;
  }
  .mu-menu-item-wrapper.active {
    color: @branding !important;
  }
}

</style>



// WEBPACK FOOTER //
// QuestionThread.vue?7350f4fb